import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import DialogForm from './DialogForm';
import { useWallet } from '../utils/wallet';
import {
  useUnlockedMnemonicAndSeed,
  verifyPassword,
  walletHasPassword,
} from '../utils/wallet-seed';
import strings from '../localization';

// Re-authenticates the user before any secret is revealed (SEC-04).
function PasswordPrompt({ onVerified, onCancel }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(false);
  // No password on file: nothing to verify, so the prompt is a plain confirm.
  const passwordless = !walletHasPassword();

  const submit = async () => {
    if (passwordless) {
      onVerified();
      return;
    }
    setChecking(true);
    const ok = await verifyPassword(password);
    setChecking(false);
    if (ok) {
      onVerified();
    } else {
      setError(true);
    }
  };

  return (
    <>
      <DialogContent>
        <DialogContentText>
          {passwordless ? strings.noPasswordSet : strings.reenterPassword}
        </DialogContentText>
        <TextField
          style={{ display: passwordless ? 'none' : undefined }}
          autoFocus
          fullWidth
          margin="normal"
          variant="outlined"
          label={strings.password}
          type="password"
          autoComplete="current-password"
          value={password}
          error={error}
          helperText={error ? strings.incorrectPassword : ''}
          onChange={(e) => {
            setPassword(e.target.value);
            setError(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{strings.cancel}</Button>
        <Button
          color="primary"
          onClick={submit}
          disabled={checking || (!passwordless && !password)}
        >
          {passwordless ? strings.continue : strings.unlock}
        </Button>
      </DialogActions>
    </>
  );
}

export default function ExportAccountDialog({ open, onClose }) {
  const wallet = useWallet();
  const [unlocked, setUnlocked] = useState(false);
  const [isHidden, setIsHidden] = useState(true);

  const close = () => {
    setUnlocked(false);
    setIsHidden(true);
    onClose();
  };

  return (
    <DialogForm open={open} onClose={close} fullWidth>
      <DialogTitle>{strings.exportAccount}</DialogTitle>
      {unlocked ? (
        <>
          <DialogContent>
            <TextField
              label={strings.privateKey}
              fullWidth
              type={isHidden ? 'password' : 'text'}
              variant="outlined"
              margin="normal"
              value={`[${Array.from(wallet.provider.account.secretKey)}]`}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!isHidden}
                  onChange={() => setIsHidden(!isHidden)}
                />
              }
              label={strings.reveal}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={close}>{strings.close}</Button>
          </DialogActions>
        </>
      ) : (
        <PasswordPrompt onVerified={() => setUnlocked(true)} onCancel={close} />
      )}
    </DialogForm>
  );
}

export function ExportMnemonicDialog({ open, onClose }) {
  const [isHidden, setIsHidden] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [mnemKey] = useUnlockedMnemonicAndSeed();

  const close = () => {
    setUnlocked(false);
    setIsHidden(true);
    onClose();
  };

  return (
    <DialogForm open={open} onClose={close} fullWidth>
      <DialogTitle>{strings.exportMnemonic}</DialogTitle>
      {unlocked ? (
        <>
          <DialogContent>
            <TextField
              label={strings.mnemonic}
              fullWidth
              type={isHidden ? 'password' : 'text'}
              variant="outlined"
              margin="normal"
              value={mnemKey.mnemonic}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!isHidden}
                  onChange={() => setIsHidden(!isHidden)}
                />
              }
              label={strings.reveal}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={close}>{strings.close}</Button>
          </DialogActions>
        </>
      ) : (
        <PasswordPrompt onVerified={() => setUnlocked(true)} onCancel={close} />
      )}
    </DialogForm>
  );
}
